import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmStore, useCrm } from '../store';
import { Card, PageTitle } from '../ui';
import { Settings, Plus, X, Music, Theater, Mic2, Package, CreditCard, RotateCcw, Trash2, Upload, Download, FileDown, Database, Loader2, ShieldCheck, FlaskConical, Sparkles, ScrollText, ArrowRight, Calendar, Building2 } from 'lucide-react';
import { downloadJson } from '../utils';
import { toast } from 'sonner';
import { exportFullBackup } from '../backup';
import { EmailsAutoCard } from '../components/EmailsAutoCard';
import { useDemoMode } from '../demoMode';
import { supabase } from '@/integrations/supabase/client';
import { useConfirm } from '../components/ConfirmDialog';
import { EmetteurEditor } from '../components/EmetteurEditor';
import { ProduitsCatalogue } from '../components/ProduitsCatalogue';
import { MentionsDocumentsEditor } from '../components/MentionsDocumentsEditor';

type ListKey = 'services' | 'domaines' | 'themes' | 'artistes' | 'options' | 'paiementModes';
type RefType = 'service' | 'domaine' | 'theme' | 'option' | 'paiement_mode';

const REF_TYPE_MAP: Partial<Record<ListKey, RefType>> = {
  services: 'service',
  domaines: 'domaine',
  themes: 'theme',
  options: 'option',
  paiementModes: 'paiement_mode',
};

const COLS: { key: ListKey; label: string; icon: typeof Music; color: string }[] = [
  { key: 'services', label: 'Services', icon: Music, color: 'text-emerald-400' },
  { key: 'domaines', label: 'Domaines', icon: Theater, color: 'text-violet-400' },
  { key: 'themes', label: "Types d'événement", icon: Sparkles, color: 'text-pink-400' },
  { key: 'artistes', label: 'Artistes', icon: Mic2, color: 'text-amber-400' },
  { key: 'options', label: 'Options', icon: Package, color: 'text-sky-400' },
  { key: 'paiementModes', label: 'Modes de paiement', icon: CreditCard, color: 'text-rose-400' },
];

export default function Parametres() {
  const data = useCrm();
  const confirm = useConfirm();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupStatus, setBackupStatus] = useState('');
  const [demo, setDemo] = useDemoMode();
  const [purging, setPurging] = useState(false);
  const [emetteurStruct, setEmetteurStruct] = useState<'cygnature' | 'suivez_vos_reves' | null>(null);

  const emetteurs = ((data as any).emetteurs || { cygnature: data.emetteur }) as Record<string, any>;
  const isEmetteurComplet = (s: 'cygnature' | 'suivez_vos_reves') => {
    const e = emetteurs[s] || {};
    return !!(e.raisonSociale && e.adresse && e.siret && e.tvaIntra && e.iban && e.bic);
  };

  const handlePurgeDemo = async () => {
    const ok = await confirm({
      title: 'Purger toutes les données du Mode Démo ?',
      description: 'Toutes les données du Mode Démo (clients, dossiers, devis, factures, etc.) seront supprimées DÉFINITIVEMENT, sans toucher aux vraies données.',
      confirmLabel: 'Purger',
      destructive: true,
    });
    if (!ok) return;
    setPurging(true);
    try {
      const { data, error } = await (supabase as any).rpc('purge_demo_data');
      if (error) throw error;
      const total = (data || []).reduce((s: number, r: any) => s + (r.deleted_count || 0), 0);
      toast.success(`Données démo purgées : ${total} enregistrement${total > 1 ? 's' : ''} supprimé${total > 1 ? 's' : ''}`);
      await crmStore.reload();
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'purge impossible'}`);
    } finally {
      setPurging(false);
    }
  };

  const handleFullBackup = async () => {
    if (backupBusy) return;
    setBackupBusy(true);
    setBackupStatus('Préparation…');
    try {
      await exportFullBackup(({ current, total, table }) => {
        setBackupStatus(`Table ${current}/${total} — ${table}`);
      });
      toast.success('Backup complet téléchargé (.zip)');
      setBackupStatus('');
    } catch (e: any) {
      console.error(e);
      toast.error(`Erreur : ${e?.message ?? 'export impossible'}`);
      setBackupStatus('');
    } finally {
      setBackupBusy(false);
    }
  };

  const handleImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        crmStore.importJson(json);
        toast.success(`Import réussi : ${json.prest?.length ?? 0} prestations`);
      } catch {
        toast.error('Fichier JSON invalide');
      }
    };
    reader.readAsText(file);
  };

  const handleBackup = () => {
    downloadJson(`cygnature-backup-${new Date().toISOString().slice(0, 10)}.json`, data);
    toast.success('Backup JSON téléchargé');
  };

  const handleExportExcel = () => {
    const headers = ['ID', 'Client', 'Tel', 'Email', 'Statut', 'Date Event', 'Lieu', 'KM', 'Durée', 'Libellé', 'Demande', 'Services', 'Domaines', 'Artistes', 'Prop TTC', 'Note'];
    const rows = data.prest.map((p) => [
      p.id, p.client, p.tel, p.email, p.statut, p.dateEvent, p.lieu, p.km ?? '', p.duree, p.libelle, p.demande,
      p.services.join('|'), p.domaines.join('|'), p.artistes.join('|'), p.propTTC ?? '', p.propNote,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Cygnature-Export-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Export Excel (CSV) téléchargé');
  };

  const add = async (key: ListKey) => {
    const v = drafts[key]?.trim();
    if (!v) return;
    const refType = REF_TYPE_MAP[key];
    if (refType) {
      try {
        await crmStore.addReferentiel(refType, v);
        toast.success(`« ${v} » ajouté`);
      } catch (e: any) {
        toast.error(`Erreur : ${e?.message || 'ajout impossible'}`);
        return;
      }
    } else {
      crmStore.update({ [key]: [...(data[key] as string[]), v].sort() } as any);
    }
    setDrafts((d) => ({ ...d, [key]: '' }));
  };
  const remove = async (key: ListKey, item: string) => {
    const refType = REF_TYPE_MAP[key];
    if (refType) {
      const ok = await confirm({
        title: `Supprimer « ${item} » ?`,
        description: 'Cet élément sera retiré de la liste pour tous les utilisateurs. Les dossiers existants qui le référencent ne sont pas modifiés.',
        confirmLabel: 'Supprimer',
        destructive: true,
      });
      if (!ok) return;
      try {
        await crmStore.removeReferentiel(refType, item);
        toast.success(`« ${item} » supprimé`);
      } catch (e: any) {
        toast.error(`Erreur : ${e?.message || 'suppression impossible'}`);
      }
    } else {
      crmStore.update({ [key]: (data[key] as string[]).filter((x) => x !== item) } as any);
    }
  };
  const rename = async (key: ListKey, oldItem: string) => {
    const refType = REF_TYPE_MAP[key];
    if (!refType) return;
    const next = window.prompt(`Renommer « ${oldItem} » en :`, oldItem);
    if (!next || next.trim() === oldItem) return;
    try {
      await crmStore.renameReferentiel(refType, oldItem, next.trim());
      toast.success('Renommé');
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'renommage impossible'}`);
    }
  };

  return (
    <div>
      <PageTitle title="Paramètres" subtitle="Gestion des référentiels et configuration" icon={<Settings className="text-amber-400" size={24} strokeWidth={1.5} />} />

      <Card className="p-5 mb-6 border-emerald-500/40 bg-emerald-500/5">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-emerald-400">
              <ShieldCheck size={14} strokeWidth={1.5} /> Backup complet de la base
            </div>
            <h3 className="text-base font-semibold text-slate-100 mb-1">Exporter toute la base de données (.zip)</h3>
            <p className="text-xs text-slate-400 leading-relaxed max-w-2xl">
              Télécharge un fichier ZIP contenant toutes les tables (clients, artistes, prestations, devis, factures, lignes, relances, paramètres, journal d'audit) au format Excel <strong>et</strong> JSON. À conserver en lieu sûr — c'est ta sauvegarde indépendante de l'infrastructure.
              {backupStatus && <span className="block mt-2 text-emerald-300 font-medium">{backupStatus}</span>}
            </p>
          </div>
          <button
            onClick={handleFullBackup}
            disabled={backupBusy}
            className="flex items-center gap-2 px-5 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/50 text-emerald-200 rounded-md text-sm font-semibold disabled:opacity-60 disabled:cursor-wait"
          >
            {backupBusy ? <Loader2 size={16} className="animate-spin" strokeWidth={1.5} /> : <Database size={16} strokeWidth={1.5} />}
            {backupBusy ? 'Export en cours…' : 'Télécharger le backup complet'}
          </button>
        </div>
      </Card>

      <ProduitsCatalogue />

      <EmailsAutoCard />

      {/* Carte Émetteur / Coordonnées légales */}
      <Card className="p-5 mb-6 border-amber-500/30">
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-amber-400">
          <Building2 size={14} strokeWidth={1.5} /> Émetteur / Coordonnées légales
        </div>
        <h3 className="text-base font-semibold text-slate-100 mb-1">Coordonnées affichées sur les devis, factures et contrats</h3>
        <p className="text-xs text-slate-400 leading-relaxed max-w-2xl mb-4">
          Renseignez ici la raison sociale, l'adresse, le SIRET, la TVA intra, le RCS, le capital, l'IBAN/BIC et la PDP de chaque structure. Tant qu'un champ obligatoire est vide, les factures affichent <code className="text-red-300">[Information à insérer]</code> en rouge.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(['cygnature', 'suivez_vos_reves'] as const).map((s) => {
            const ok = isEmetteurComplet(s);
            return (
              <button
                key={s}
                onClick={() => setEmetteurStruct(s)}
                className={`text-left p-4 rounded-lg border transition-colors ${ok ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10' : 'border-red-500/30 bg-red-500/5 hover:bg-red-500/10'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-100">
                    {s === 'cygnature' ? 'Cygnature Production (SASU)' : 'Suivez Vos Rêves Event (Asso)'}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                    {ok ? 'Complet' : 'À compléter'}
                  </span>
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {emetteurs[s]?.raisonSociale || '—'} · {emetteurs[s]?.siret || 'SIRET manquant'}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {emetteurStruct && <EmetteurEditor initialStructure={emetteurStruct} onClose={() => setEmetteurStruct(null)} />}


      {/* Carte Synchronisation agenda Google */}
      <MentionsDocumentsEditor />

      <Card className="p-5 mb-6 border-sky-500/30">
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-sky-400">
          <Calendar size={14} strokeWidth={1.5} /> Synchronisation Google Agenda
        </div>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <h3 className="text-base font-semibold text-slate-100 mb-1">
              Connecter mon agenda Google
              <span className="ml-2 inline-block px-2 py-0.5 text-[10px] bg-amber-500/20 text-amber-300 rounded-full font-bold uppercase tracking-wider border border-amber-500/30">
                Bientôt disponible
              </span>
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed max-w-2xl">
              Une fois connecté, chaque contrat signé créera automatiquement un événement dans <strong>ton propre agenda Google</strong> (date, lieu, client, type de prestation). Chaque utilisateur connecte son agenda depuis cet écran — les événements vont dans l'agenda de la personne qui a signé le contrat.
            </p>
            <p className="text-[11px] text-slate-500 mt-2 italic">
              En attente de la configuration initiale (identifiants Google). Le bouton s'activera automatiquement dès que ce sera prêt.
            </p>
          </div>
          <button
            disabled
            className="flex items-center gap-2 px-5 py-3 bg-slate-700/40 border border-slate-600/40 text-slate-400 rounded-md text-sm font-semibold cursor-not-allowed opacity-70"
            title="En attente de la configuration initiale"
          >
            <Calendar size={16} strokeWidth={1.5} />
            Connecter mon agenda
          </button>
        </div>
      </Card>


      {/* Carte Mode Démo / Sandbox */}
      <Card className={`p-5 mb-6 ${demo ? 'border-orange-500/40' : 'border-slate-700/40'}`}>
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-orange-400">
          <FlaskConical size={14} strokeWidth={1.5} /> Mode Démo / Sandbox
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-slate-100 mb-1">
              Espace de test isolé {demo ? <span className="ml-2 inline-block px-2 py-0.5 text-[10px] bg-orange-500/30 text-orange-200 rounded-full font-bold uppercase tracking-wider">Actif</span> : null}
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed max-w-2xl">
              Le Mode Démo te permet de tester librement l'outil (créer des dossiers, devis, factures…) <strong>sans polluer tes vraies données</strong>. Les deux espaces sont totalement étanches.
              Bascule depuis le bouton en haut de la barre latérale gauche, ou ici :
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <button
                onClick={() => setDemo(!demo)}
                className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors ${
                  demo
                    ? 'bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200'
                    : 'bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 text-orange-200'
                }`}
              >
                {demo ? '← Revenir au Mode Réel' : '🧪 Activer le Mode Démo'}
              </button>
              <button
                onClick={handlePurgeDemo}
                disabled={purging}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-md text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                title="Réservé aux administrateurs et à la direction"
              >
                {purging ? <Loader2 size={13} className="animate-spin" strokeWidth={1.5} /> : <Trash2 size={13} strokeWidth={1.5} />}
                Vider toutes les données démo
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-6 border-amber-500/30">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-amber-400">
          <Upload size={14} strokeWidth={1.5} /> Outils locaux (export rapide / import JSON legacy)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 rounded-md text-sm font-medium"
          >
            <FileDown size={16} strokeWidth={1.5} /> Export Excel (.csv)
          </button>
          <button
            onClick={handleBackup}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/40 text-sky-300 rounded-md text-sm font-medium"
          >
            <Download size={16} strokeWidth={1.5} /> Télécharger un backup (.json)
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 rounded-md text-sm font-medium"
          >
            <Upload size={16} strokeWidth={1.5} /> Importer un backup (.json)
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Importez votre fichier <code className="text-amber-300">cygnature-backup-*.json</code> pour charger vos 81 prestations. Le fichier remplace les données actuelles.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleImport(f);
            e.target.value = '';
          }}
        />
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {COLS.map(({ key, label, icon: Icon, color }) => {
          const list = data[key] as string[];
          return (
            <Card key={key} className="p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-slate-400">
                <Icon size={14} className={color} strokeWidth={1.5} /> {label} ({list.length})
              </div>
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1 mb-3">
                {list.map((item) => (
                  <div key={item} className="flex items-center justify-between bg-slate-950/50 rounded-md px-2.5 py-1.5 text-xs text-slate-300">
                    <button
                      onClick={() => REF_TYPE_MAP[key] && rename(key, item)}
                      className={`truncate text-left flex-1 ${REF_TYPE_MAP[key] ? 'hover:text-amber-300 cursor-pointer' : 'cursor-default'}`}
                      title={REF_TYPE_MAP[key] ? 'Cliquer pour renommer' : undefined}
                    >
                      {item}
                    </button>
                    <button onClick={() => remove(key, item)} className="text-red-400 hover:text-red-300 p-0.5 ml-2">
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5 mt-auto">
                <input
                  value={drafts[key] ?? ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && add(key)}
                  placeholder={key === 'paiementModes' ? 'Nouveau mode (ex: Lydia, V…)' : 'Ajouter…'}
                  className="flex-1 px-2.5 py-1.5 bg-slate-950/60 border border-slate-800 rounded-md text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
                />
                <button onClick={() => add(key)} className="px-2.5 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-md hover:bg-amber-500/30">
                  <Plus size={13} strokeWidth={1.5} />
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5 mt-6 max-w-md border-amber-500/30">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-amber-400">
          <RotateCcw size={14} strokeWidth={1.5} /> Données du CRM
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              crmStore.resetToSeed();
              toast.success('Données initiales rechargées (81 prestations)');
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 rounded-md text-xs"
          >
            <RotateCcw size={13} strokeWidth={1.5} /> Recharger les 81 prestations initiales
          </button>
          <button
            onClick={async () => {
              const ok = await confirm({
                title: 'Vider toutes les prestations ?',
                description: 'Toutes les données seront effacées localement.',
                confirmLabel: 'Vider',
                destructive: true,
              });
              if (ok) {
                crmStore.reset();
                toast.success('CRM vidé');
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-md text-xs"
          >
            <Trash2 size={13} strokeWidth={1.5} /> Vider toutes les données
          </button>
        </div>
      </Card>

      <Card className="p-5 mt-6 border-slate-700/40">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-slate-400">
              <ScrollText size={14} strokeWidth={1.5} /> Journal d'activité
            </div>
            <h3 className="text-base font-semibold text-slate-100 mb-1">Audit trail du CRM</h3>
            <p className="text-xs text-slate-400 leading-relaxed max-w-2xl">
              Historique chronologique de toutes les actions (créations, modifications, suppressions). Utile pour l'audit et la traçabilité.
            </p>
          </div>
          <Link
            to="../journal"
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 rounded-md text-sm font-medium"
          >
            Ouvrir le journal <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </Card>
    </div>
  );
}
